import React, { useMemo } from 'react';
import './MonthCalendar.css';
import {
  zonedStartOfDay,
  moveByDays,
  formatDateKey,
  getZonedDateParts,
  getZonedWeekdayIndex,
  getOsloToday
} from './timezone';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function MonthCalendar({
  year,
  month,
  days,
  selectedDate,
  onSelectDate
}) {
  const dayMap = useMemo(() => {
    const map = new Map();
    (days || []).forEach(entry => {
      if (entry && typeof entry.date === 'string') {
        const key = entry.date;
        const count = Number.isFinite(entry.count) ? entry.count : 0;
        map.set(key, count);
      }
    });
    return map;
  }, [days]);

  const monthStart = useMemo(() => zonedStartOfDay(year, month, 1), [year, month]);
  const nextMonthStart = useMemo(() => {
    const total = year * 12 + (month - 1) + 1;
    const nextYear = Math.floor(total / 12);
    const nextMonth = (total % 12) + 1;
    return zonedStartOfDay(nextYear, nextMonth, 1);
  }, [year, month]);

  const startWeekday = useMemo(() => getZonedWeekdayIndex(monthStart), [monthStart]);
  const gridStart = useMemo(() => moveByDays(monthStart, -startWeekday), [monthStart, startWeekday]);
  const todayKey = useMemo(() => formatDateKey(getOsloToday()), []);
  const monthKey = `${year}-${String(month).padStart(2, '0')}`;

  const weeks = useMemo(() => {
    const output = [];
    let cursor = gridStart;
    for (let weekIndex = 0; weekIndex < 6; weekIndex += 1) {
      const weekDays = [];
      for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
        const dateKey = formatDateKey(cursor);
        const parts = getZonedDateParts(cursor);
        const count = dayMap.get(dateKey) || 0;
        const isOutside = `${parts.year}-${String(parts.month).padStart(2, '0')}` !== monthKey;
        weekDays.push({
          iso: dateKey,
          label: String(parts.day).padStart(2, '0'),
          isOutside,
          count
        });
        cursor = moveByDays(cursor, 1);
      }
      output.push({ id: `week-${weekIndex}`, days: weekDays });
      const reachedEnd = cursor.getTime() >= nextMonthStart.getTime() && getZonedWeekdayIndex(cursor) === 0;
      if (reachedEnd && weekIndex >= 4) {
        break;
      }
    }
    return output;
  }, [dayMap, gridStart, monthKey, nextMonthStart]);

  return (
    <div className="shipments-calendar">
      <div className="shipments-calendar-header">
        {WEEKDAY_LABELS.map(label => (
          <div key={label}>{label}</div>
        ))}
      </div>
      {weeks.map(week => (
        <div key={week.id} className="shipments-calendar-week">
          {week.days.map(day => {
            const isToday = day.iso === todayKey;
            const isSelected = day.iso === selectedDate;
            const dayClass = [
              'shipments-calendar-day',
              day.isOutside ? 'is-outside' : '',
              isToday ? 'is-today' : '',
              isSelected ? 'is-selected' : ''
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div
                key={day.iso}
                className={dayClass}
                onClick={() => onSelectDate && onSelectDate(day.iso)}
                role="button"
                tabIndex={0}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectDate?.(day.iso);
                  }
                }}
              >
                <span className="day-number">{day.label}</span>
                <h3>{day.count}</h3>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
